'use client';

import { suggestEmail } from '@/lib/utils/email-suggest';

interface EmailTypoHintProps {
  /** Current value of the email field. */
  email: string;
  /** Called with the corrected address when the person accepts the suggestion. */
  onAccept: (corrected: string) => void;
  /** Colour for the clickable correction. Defaults to the surface's accent. */
  accentColor?: string;
}

/**
 * "Did you mean someone@gmail.com?" under an email field.
 *
 * Renders nothing unless the address looks mistyped, and never blocks the form:
 * a real address on an unfamiliar domain has to go through untouched, so this
 * offers a correction rather than refusing the one that was typed.
 *
 * It matters more here than on most forms. Nothing bounces back to the person
 * who typed it: the photo link, the ticket and the start-time email all go to
 * the address as written, and they find out on race morning.
 */
export function EmailTypoHint({ email, onAccept, accentColor }: EmailTypoHintProps) {
  const suggestion = suggestEmail(email);
  if (!suggestion) return null;

  return (
    <p
      style={{
        fontSize: '13px',
        lineHeight: 1.5,
        margin: '6px 0 0',
        color: 'var(--fv-text-secondary)',
        // The surrounding form's face (v8 body on reskinned pages, the event's
        // font on themed ones), not a hard-wired old font.
        fontFamily: 'inherit',
      }}
    >
      Did you mean{' '}
      <button
        type="button"
        onClick={() => onAccept(suggestion)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontFamily: 'inherit',
          fontWeight: 700,
          color: accentColor || 'var(--fv-accent)',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {suggestion}
      </button>
      ?
    </p>
  );
}
